/**
 * TranscriptTimeline - ブロック見出し + 1行コンパクト発話のタイムライン
 *
 * frontend/src/components/TranscriptTimeline.tsx からの移植（音声再生関連は削除）。
 * 選択中発話のスクロール、ブロック単位のスクロール、ViewMode 切替に対応。
 */

import { useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import type { TimelineBlock, Utterance } from '@/types/review';
import { CompactUtterance } from './CompactUtterance';
import type { ViewMode } from './ViewModeToggle';

interface TranscriptTimelineProps {
  blocks: TimelineBlock[];
  scrollToBlock?: string;
  selectedUtteranceId?: string;
  viewMode: ViewMode;
  onUtteranceSelect: (utterance: Utterance) => void;
  showFeedbackRole?: boolean;
  /**
   * supporting ロールのハイライト対象 utterance_id の whitelist。
   * undefined: 全 supporting を色分け / Set: 含まれる発話のみ（O(1) ルックアップ）
   */
  supportingUtteranceIdsWhitelist?: Set<string>;
  /** utterance_id → 指摘番号（1-indexed）配列 */
  feedbackIndicesByUtteranceId?: Map<string, number[]>;
}

export interface TranscriptTimelineHandle {
  scrollToBlock: (blockName: string) => void;
  scrollToUtterance: (utteranceId: string) => void;
}

export const TranscriptTimeline = forwardRef<TranscriptTimelineHandle, TranscriptTimelineProps>(
  function TranscriptTimeline(
    {
      blocks,
      scrollToBlock,
      selectedUtteranceId,
      viewMode,
      onUtteranceSelect,
      showFeedbackRole = true,
      supportingUtteranceIdsWhitelist,
      feedbackIndicesByUtteranceId,
    },
    ref,
  ) {
    const blockRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const utteranceRefs = useRef<Map<string, HTMLDivElement>>(new Map());
    const containerRef = useRef<HTMLDivElement>(null);

    const scrollToBlockFn = (blockName: string) => {
      const element = blockRefs.current.get(blockName);
      if (element && containerRef.current) {
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;
        containerRef.current.scrollBy({
          top: elementTop - containerTop - 16,
          behavior: 'smooth',
        });
      }
    };

    const scrollToUtteranceFn = (utteranceId: string) => {
      const element = utteranceRefs.current.get(utteranceId);
      if (element && containerRef.current) {
        const containerTop = containerRef.current.getBoundingClientRect().top;
        const elementTop = element.getBoundingClientRect().top;
        containerRef.current.scrollBy({
          top: elementTop - containerTop - 100,
          behavior: 'smooth',
        });
      }
    };

    useImperativeHandle(ref, () => ({
      scrollToBlock: scrollToBlockFn,
      scrollToUtterance: scrollToUtteranceFn,
    }));

    useEffect(() => {
      if (scrollToBlock) scrollToBlockFn(scrollToBlock);
    }, [scrollToBlock]);

    return (
      <div ref={containerRef} className="flex-1 overflow-y-auto bg-white">
        <div className="divide-y divide-gray-100">
          {blocks.map((block) => (
            <BlockCompactSection
              key={block.name}
              block={block}
              viewMode={viewMode}
              selectedUtteranceId={selectedUtteranceId}
              onUtteranceSelect={onUtteranceSelect}
              showFeedbackRole={showFeedbackRole}
              supportingUtteranceIdsWhitelist={supportingUtteranceIdsWhitelist}
              feedbackIndicesByUtteranceId={feedbackIndicesByUtteranceId}
              ref={(el) => {
                if (el) blockRefs.current.set(block.name, el);
              }}
              utteranceRefs={utteranceRefs}
            />
          ))}
        </div>
      </div>
    );
  },
);

interface BlockCompactSectionProps {
  block: TimelineBlock;
  viewMode: ViewMode;
  selectedUtteranceId?: string;
  onUtteranceSelect: (utterance: Utterance) => void;
  utteranceRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  showFeedbackRole?: boolean;
  supportingUtteranceIdsWhitelist?: Set<string>;
  feedbackIndicesByUtteranceId?: Map<string, number[]>;
}

const BlockCompactSection = forwardRef<HTMLDivElement, BlockCompactSectionProps>(
  function BlockCompactSection(
    {
      block,
      viewMode,
      selectedUtteranceId,
      onUtteranceSelect,
      utteranceRefs,
      showFeedbackRole = true,
      supportingUtteranceIdsWhitelist,
      feedbackIndicesByUtteranceId,
    },
    ref,
  ) {
    const hasIssues = block.issueCount > 0;
    const utterancesToShow =
      viewMode === 'feedback-only' ? block.utterances.filter((u) => u.feedback) : block.utterances;
    const skippedCount = block.utterances.length - utterancesToShow.length;

    return (
      <div ref={ref} className="scroll-mt-4">
        <div
          className={`sticky top-0 z-10 flex items-center justify-between px-3 py-2 border-b ${
            hasIssues ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'
          }`}
        >
          <div className="flex items-center">
            <span
              className={`w-2 h-2 rounded-full mr-2 ${hasIssues ? 'bg-red-500' : 'bg-green-500'}`}
            />
            <h2 className="text-sm font-semibold text-gray-900">{block.name}</h2>
            <span className="ml-2 text-xs font-mono text-gray-500">{block.startTime}</span>
          </div>
          {hasIssues && (
            <svg className="w-3.5 h-3.5 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          )}
        </div>

        <div className="divide-y divide-gray-50">
          {viewMode === 'feedback-only' && skippedCount > 0 && utterancesToShow.length === 0 && (
            <div className="px-4 py-2 text-center text-xs text-gray-400 bg-gray-50">
              ...{block.utterances.length}件の発言（FBなし）...
            </div>
          )}
          {utterancesToShow.map((utterance, index) => {
            const showSkippedBefore =
              viewMode === 'feedback-only' && index === 0 && skippedCount > 0;
            const hasAnyFeedback = !!utterance.feedback;
            const hasExplicitRole = !!utterance.feedbackRole || !!utterance.feedback?.role;
            const isPrimary =
              utterance.feedbackRole === 'primary' || utterance.feedback?.role === 'primary';
            const isSupporting =
              utterance.feedbackRole === 'supporting' || utterance.feedback?.role === 'supporting';
            const primaryFallback = !hasExplicitRole && hasAnyFeedback;
            const supportingPasses =
              supportingUtteranceIdsWhitelist === undefined
                ? isSupporting
                : isSupporting && supportingUtteranceIdsWhitelist.has(utterance.id);
            const feedbackIndices = feedbackIndicesByUtteranceId?.get(utterance.id);

            return (
              <div key={utterance.id}>
                {showSkippedBefore && (
                  <div className="px-4 py-1 text-center text-xs text-gray-400 bg-gray-50">
                    ...{skippedCount}件の発言（FBなし）...
                  </div>
                )}
                <div
                  ref={(el) => {
                    if (el) utteranceRefs.current.set(utterance.id, el);
                  }}
                >
                  <CompactUtterance
                    utterance={utterance}
                    isSelected={selectedUtteranceId === utterance.id}
                    hasPrimaryFeedback={showFeedbackRole && (isPrimary || primaryFallback)}
                    hasSupportingFeedback={showFeedbackRole && supportingPasses}
                    onClick={() => onUtteranceSelect(utterance)}
                    feedbackIndices={feedbackIndices}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  },
);
